import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ContactForm from "@/pages/about/components/ContactForm";
import { siteInfo, starProfile, starRoles } from "@/mocks/star";

const scopeCards = [
  {
    icon: "ri-book-2-line",
    title: "内容范围",
    text: "收录叶山小百合参演的电影、电视剧作品资料，包括剧情简介、演职人员、上映信息与剧照。",
  },
  {
    icon: "ri-shield-check-line",
    title: "整理原则",
    text: "所有条目均按作品维度归档，列表与详情数据保持一致，方便影迷与研究者交叉核对。",
  },
  {
    icon: "ri-information-line",
    title: "使用说明",
    text: "本站为影迷资料整理站点，仅提供作品介绍与资讯展示，不提供影片播放或下载服务。",
  },
];

export default function AboutPage() {
  const infoRows = [
    { label: "姓名", value: starProfile.name },
    { label: "罗马音", value: starProfile.romajiName },
    { label: "出生日期", value: starProfile.birthday },
    { label: "出生地", value: starProfile.birthplace },
    { label: "身高", value: starProfile.height },
    { label: "出道年份", value: starProfile.debut },
    { label: "所属事务所", value: starProfile.agency },
  ];

  return (
    <div className="pt-24 md:pt-28">
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-7xl mx-auto w-full px-4 md:px-8 py-8 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于本站" }]} />
          <h1 className="mt-5 font-heading text-2xl md:text-4xl font-semibold text-foreground-950">
            关于本站
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            {siteInfo.intro}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 md:px-8 py-12 md:py-16 space-y-16 md:space-y-20">
        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
            网站定位与内容范围
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {scopeCards.map((card) => (
              <div
                key={card.title}
                className="p-6 rounded-lg border border-background-200 bg-background-50"
              >
                <span className="w-12 h-12 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center mb-4">
                  <i className={`${card.icon} text-2xl`}></i>
                </span>
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                  {card.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{card.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8 lg:gap-12">
          <div className="w-full max-w-[380px] mx-auto lg:mx-0">
            <div className="relative w-full h-[460px] md:h-[500px] rounded-lg overflow-hidden bg-background-200 border border-background-200">
              <img
                src={starProfile.avatar}
                alt={`${starProfile.name} 肖像`}
                title={`${starProfile.name} - 影视明星资料`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div>
            <p className="text-xs md:text-sm font-medium text-accent-700 tracking-widest mb-2">
              STAR PROFILE
            </p>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
              {starProfile.name} 明星简介
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">
              {starProfile.intro}
            </p>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-2 text-sm border-b border-background-100 pb-2.5">
                  <dt className="text-foreground-500 shrink-0 w-20">{row.label}</dt>
                  <dd className="text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>

            <blockquote className="mt-6 pl-5 border-l-2 border-accent-400 text-foreground-800 text-sm md:text-base leading-relaxed italic">
              {starProfile.belief}
            </blockquote>
          </div>
        </section>

        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
            演艺历程
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {starProfile.timeline.map((item) => (
              <div
                key={item.year}
                className="relative p-5 rounded-lg border border-background-200 bg-background-50"
              >
                <span className="inline-flex px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs font-medium mb-3">
                  {item.year}
                </span>
                <p className="text-sm text-foreground-700 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
            代表角色
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {starRoles.map((role) => (
              <div
                key={role.title}
                className="p-5 rounded-lg bg-primary-950 text-background-50"
              >
                <span className="w-10 h-10 rounded-lg bg-background-50/10 text-accent-400 flex items-center justify-center mb-4">
                  <i className="ri-user-star-line text-xl"></i>
                </span>
                <h3 className="font-heading text-sm md:text-base font-semibold">{role.title}</h3>
                <p className="mt-1.5 text-xs text-background-50/65">{role.note}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8 lg:gap-12">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
              联系方式
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-6">
              如果你发现资料有误，或希望补充作品信息，欢迎通过以下方式联系我们，也可直接提交右侧留言。
            </p>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">邮箱</span>
                  <span className="block text-sm text-foreground-900">{siteInfo.email}</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">地址</span>
                  <span className="block text-sm text-foreground-900">{siteInfo.address}</span>
                </span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
              留言反馈
            </h2>
            <ContactForm />
          </div>
        </section>

        <div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 h-11 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}